import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Users, Mail, Bell, MessageSquare, Loader2, Eye, MapPin, Store, X, Smartphone } from 'lucide-react';
import { Card, Badge } from './AdminPanelPrimitives';
import { EmptyState } from './adminDesignSystem';
import { AdminAutocomplete } from './AdminAutocomplete';
import { PromoteTonightCard } from './PromoteTonightCard';
import { DevicePlatformWaitlistCard } from './DevicePlatformWaitlistCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE_OPTIONS = [
  { id: 'all', label: 'Everyone' },
  { id: 'role', label: 'By role' },
  { id: 'city', label: 'By city' },
  { id: 'device_platform', label: 'By device' },
  { id: 'users', label: 'Specific users' },
];

const DEVICE_PLATFORMS = [
  { id: 'ios', label: 'iPhone (iOS)' },
  { id: 'android', label: 'Android' },
  { id: 'desktop', label: 'Desktop' },
];

const ROLES = [
  { id: 'guest', label: 'Guests' },
  { id: 'host', label: 'Hosts' },
  { id: 'venue_owner', label: 'Venue owners' },
  { id: 'admin', label: 'Admins' },
  { id: 'staff', label: 'Staff' },
];

const ROLE_BADGE_TONE = {
  guest: '',
  host: 'blue',
  venue_owner: 'green',
  admin: 'red',
  staff: 'orange',
};

const ROLE_LABEL = (role) => ({
  guest: 'Guest', host: 'Host', venue_owner: 'Venue owner', admin: 'Admin', staff: 'Staff',
}[role] || role || 'User');

export const AdminBroadcastsPanel = ({ headers }) => {
  const [history, setHistory] = useState([]);
  const [audienceType, setAudienceType] = useState('all');
  const [role, setRole] = useState('guest');
  const [devicePlatform, setDevicePlatform] = useState('ios');
  const [pickedCity, setPickedCity] = useState(null); // { city, country, venue_count }
  const [pickedUsers, setPickedUsers] = useState([]); // [{id, email, name, role, ...}]
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [channels, setChannels] = useState({ in_app: true, push: true, email: false });
  const [recipientCount, setRecipientCount] = useState(null);
  const [recipientBreakdown, setRecipientBreakdown] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/broadcasts`, { headers });
      setHistory(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load broadcasts');
    }
  }, [headers]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const audiencePayload = useMemo(() => {
    if (audienceType === 'all') return { type: 'all' };
    if (audienceType === 'role') return { type: 'role', role };
    if (audienceType === 'city') return { type: 'city', city: pickedCity?.city || '' };
    if (audienceType === 'device_platform') return { type: 'device_platform', device_platform: devicePlatform };
    if (audienceType === 'users') return { type: 'users', user_ids: pickedUsers.map((u) => u.id) };
    return { type: 'all' };
  }, [audienceType, role, pickedCity, devicePlatform, pickedUsers]);

  const resetRecipientPreview = useCallback(() => {
    setRecipientCount(null);
    setRecipientBreakdown(null);
  }, []);

  const previewRecipients = async () => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/admin/broadcasts/preview`, { audience: audiencePayload }, { headers });
      setRecipientCount(res.data?.recipient_count ?? 0);
      setRecipientBreakdown(res.data?.breakdown || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview recipients');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendBroadcast = async () => {
    if (title.trim().length < 1 || message.trim().length < 1) {
      toast.error('Title and message are required.');
      return;
    }
    if (audienceType === 'city' && !pickedCity) {
      toast.error('Pick a city first.');
      return;
    }
    if (audienceType === 'users' && pickedUsers.length === 0) {
      toast.error('Pick at least one user.');
      return;
    }
    const selected = Object.keys(channels).filter((k) => channels[k]);
    if (!selected.length) {
      toast.error('Pick at least one delivery channel.');
      return;
    }
    if (recipientCount === null) {
      toast.error('Please preview recipients first.');
      return;
    }
    if (recipientCount === 0) {
      toast.error('Audience is empty — refine your selection.');
      return;
    }
    if (!window.confirm(`Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'} via ${selected.join(' + ')}?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcasts`, {
        title: title.trim(),
        message: message.trim(),
        channels: selected,
        audience: audiencePayload,
      }, { headers });
      toast.success(`Broadcast sent · ${res.data?.recipient_count || 0} recipients`);
      setTitle('');
      setMessage('');
      resetRecipientPreview();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  // ---- Renderers for autocomplete suggestions ----

  const renderCityRow = (row) => (
    <>
      <MapPin size={14} color="#5B7EFF" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{row.city}</span>
        <span style={{ fontSize: 11, color: '#777' }}>
          {row.country || '—'} · {row.venue_count} venue{row.venue_count === 1 ? '' : 's'}
        </span>
      </div>
    </>
  );

  const renderUserRow = (row) => (
    <>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', background: '#EEE',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
      }}>
        {row.avatar_url ? <img src={row.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Store size={14} color="#777" />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{row.name || row.email}</span>
        <span style={{ fontSize: 11, color: '#777' }}>{row.email}</span>
      </div>
      <Badge text={ROLE_LABEL(row.role)} color={ROLE_BADGE_TONE[row.role]} />
    </>
  );

  const addPickedUser = (row) => {
    if (pickedUsers.some((u) => u.id === row.id)) {
      toast.message('Already added');
      return;
    }
    setPickedUsers((prev) => [...prev, row]);
    resetRecipientPreview();
  };
  const removePickedUser = (id) => {
    setPickedUsers((prev) => prev.filter((u) => u.id !== id));
    resetRecipientPreview();
  };
  const pickCity = (row) => {
    setPickedCity(row);
    resetRecipientPreview();
  };
  const clearCity = () => {
    setPickedCity(null);
    resetRecipientPreview();
  };

  return (
    <div data-testid="admin-broadcasts-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PromoteTonightCard headers={headers} />
      <DevicePlatformWaitlistCard headers={headers} />

      <Card style={{ padding: 18 }}>
        <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22 }}>Broadcasts</h2>
        <p style={{ margin: '4px 0 14px', fontSize: 13, color: '#666' }}>
          Send a one-shot message to a targeted audience. Channels are picked per send. Email defaults OFF.
        </p>

        {/* Audience */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Audience</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {AUDIENCE_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => { setAudienceType(o.id); resetRecipientPreview(); }}
                data-testid={`admin-broadcast-audience-${o.id}`}
                style={{
                  padding: '8px 14px', borderRadius: 999, border: '1px solid #E7E7E7', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  background: audienceType === o.id ? '#111' : '#FFF',
                  color: audienceType === o.id ? '#FFF' : '#444',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Role selector */}
          {audienceType === 'role' ? (
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { setRole(r.id); resetRecipientPreview(); }}
                  data-testid={`admin-broadcast-role-${r.id}`}
                  style={{
                    padding: '6px 12px', borderRadius: 999, fontWeight: 700, fontSize: 12,
                    border: `1px solid ${role === r.id ? '#111' : '#E7E7E7'}`,
                    background: role === r.id ? '#111' : '#FFF',
                    color: role === r.id ? '#FFF' : '#444',
                    cursor: 'pointer',
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          ) : null}

          {/* City picker */}
          {audienceType === 'city' ? (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                Start typing a city. We target users who are tied to that city: profile city, venue owners + listing hosts there, and guests with a recent transaction at a venue in that city (last 90 days).
              </p>
              {pickedCity ? (
                <div data-testid="admin-broadcast-city-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 999, background: '#EEF2FF', border: '1px solid #C7D0FF', alignSelf: 'flex-start' }}>
                  <MapPin size={12} color="#5B7EFF" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2945' }}>{pickedCity.city}</span>
                  <span style={{ fontSize: 11, color: '#5B7EFF' }}>{pickedCity.country || ''} · {pickedCity.venue_count} venues</span>
                  <button onClick={clearCity} data-testid="admin-broadcast-city-clear" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={12} /></button>
                </div>
              ) : (
                <AdminAutocomplete
                  endpoint="/admin/directory/cities"
                  headers={headers}
                  placeholder="Search cities… (e.g. Corfu, Athens, Glyfada)"
                  minChars={0}
                  renderRow={renderCityRow}
                  onSelect={pickCity}
                  emptyHint="No matching cities — only cities with venues/listings appear here."
                  dataTestId="admin-broadcast-city-autocomplete"
                />
              )}
            </div>
          ) : null}

          {/* Device platform picker */}
          {audienceType === 'device_platform' ? (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                Target users by the device they signed in from. iPhone (iOS) users are the
                people you want to ping the moment the App Store version is live.
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DEVICE_PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setDevicePlatform(p.id); resetRecipientPreview(); }}
                    data-testid={`admin-broadcast-device-${p.id}`}
                    style={{
                      padding: '6px 12px', borderRadius: 999, fontWeight: 700, fontSize: 12,
                      border: `1px solid ${devicePlatform === p.id ? '#111' : '#E7E7E7'}`,
                      background: devicePlatform === p.id ? '#111' : '#FFF',
                      color: devicePlatform === p.id ? '#FFF' : '#444',
                      cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <Smartphone size={11} /> {p.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Specific users picker */}
          {audienceType === 'users' ? (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                Search by email or name. You can pick guests, hosts, venue owners, admins or staff. Selected users appear below as chips.
              </p>
              <AdminAutocomplete
                endpoint="/admin/directory/users"
                headers={headers}
                placeholder="Search users by email or name…"
                renderRow={renderUserRow}
                onSelect={addPickedUser}
                emptyHint="No matching users"
                dataTestId="admin-broadcast-users-autocomplete"
                clearOnSelect={true}
              />
              {pickedUsers.length > 0 ? (
                <div data-testid="admin-broadcast-picked-users" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {pickedUsers.map((u) => (
                    <div key={u.id} data-testid={`admin-broadcast-picked-user-${u.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: '#F4F5F8', border: '1px solid #E7E7E7' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>{u.name || u.email}</span>
                      <span style={{ fontSize: 11, color: '#777' }}>· {ROLE_LABEL(u.role)}</span>
                      <button onClick={() => removePickedUser(u.id)} data-testid={`admin-broadcast-picked-user-remove-${u.id}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Channels */}
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Delivery channels</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { id: 'in_app', label: 'In-app', icon: MessageSquare },
              { id: 'push', label: 'Push', icon: Bell },
              { id: 'email', label: 'Email', icon: Mail },
            ].map((c) => {
              const Icon = c.icon;
              const active = channels[c.id];
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setChannels((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                  data-testid={`admin-broadcast-channel-${c.id}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                    border: `1px solid ${active ? '#111' : '#E7E7E7'}`,
                    background: active ? '#111' : '#FFF',
                    color: active ? '#FFF' : '#444',
                  }}
                >
                  <Icon size={12} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Message */}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-broadcast-title-input" placeholder="e.g. New venues launched in Athens" style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 14 }} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Message
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="admin-broadcast-message-input" rows={4} placeholder="Up to 600 characters" style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 13.5, resize: 'vertical' }} />
          </label>
        </div>

        {/* Preview + Send */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={previewRecipients}
            disabled={previewLoading}
            data-testid="admin-broadcast-preview-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', border: '1px solid #E7E7E7', background: '#FFF', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview recipients
          </button>
          {recipientCount !== null ? (
            <span data-testid="admin-broadcast-recipient-count" style={{ fontSize: 13, color: '#444', fontWeight: 600 }}>
              <Users size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
              {recipientCount.toLocaleString()} recipient{recipientCount === 1 ? '' : 's'}
            </span>
          ) : null}
          <button
            type="button"
            onClick={sendBroadcast}
            disabled={sending || recipientCount === null || recipientCount === 0}
            data-testid="admin-broadcast-send-button"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 12, border: 'none', cursor: sending ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13,
              background: !recipientCount ? '#9aa0a6' : '#DD0608', color: '#FFF',
            }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send broadcast
          </button>
        </div>
        {recipientBreakdown && audienceType === 'city' ? (
          <div data-testid="admin-broadcast-city-breakdown" style={{ marginTop: 10, padding: 10, borderRadius: 10, background: '#F4F5F8', fontSize: 12, color: '#444', lineHeight: 1.5 }}>
            <strong>How we found them:</strong>{' '}
            {recipientBreakdown.profile_city || 0} with profile city{', '}
            {recipientBreakdown.venue_owners || 0} venue owner{(recipientBreakdown.venue_owners || 0) === 1 ? '' : 's'}{', '}
            {recipientBreakdown.host_owners || 0} host{(recipientBreakdown.host_owners || 0) === 1 ? '' : 's'} with listings here{', '}
            {recipientBreakdown.active_guests || 0} guest{(recipientBreakdown.active_guests || 0) === 1 ? '' : 's'} active in the last 90 days.
          </div>
        ) : null}
      </Card>

      <Card style={{ padding: 18 }}>
        <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18 }}>Recent broadcasts</h3>
        <div data-testid="admin-broadcast-history" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {history.length === 0 ? (
            <EmptyState
              icon={Send}
              tone="info"
              title="No broadcasts sent yet"
              body="Use the form above to send a one-off push, in-app or email blast to everyone, a single role, a city, or a hand-picked list of users."
              dataTestId="admin-broadcasts-empty-state"
            />
          ) : null}
          {history.map((b) => (
            <div key={b.id} data-testid={`admin-broadcast-row-${b.id}`} style={{ padding: 12, borderRadius: 14, border: '1px solid #E7E7E7' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{b.title}</p>
                <span style={{ fontSize: 11, color: '#888' }}>{new Date(b.created_at).toLocaleString()}</span>
              </div>
              <p style={{ margin: '4px 0 8px', fontSize: 13, color: '#555' }}>{b.message}</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(b.channels || []).map((c) => <Badge key={c} text={c} color="blue" />)}
                <Badge text={`${b.recipient_count} sent`} color="green" />
                <Badge text={
                  b.audience?.type === 'city' ? `city: ${b.audience.city}`
                    : b.audience?.type === 'role' ? `role: ${b.audience.role}`
                    : b.audience?.type === 'device_platform' ? `device: ${b.audience.device_platform}`
                    : b.audience?.type === 'users' ? `${(b.audience.user_ids || []).length} users`
                    : 'everyone'
                } color="" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AdminBroadcastsPanel;
